import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';

export default function CombustiveisPage() {
  const { canManageCadastros } = useAuth();
  const canEdit = canManageCadastros();
  
  const [combustiveis, setCombustiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ type: '', unit: 'Litro' });
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/combustiveis');
      setCombustiveis(res.data);
    } catch (error) {
      toast.error('Erro ao carregar combustíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/combustiveis/${editingId}`, formData);
        toast.success('Combustível atualizado!');
      } else {
        await api.post('/combustiveis', formData);
        toast.success('Combustível cadastrado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar combustível');
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir?')) return;
    try {
      await api.delete(`/combustiveis/${id}`);
      toast.success('Combustível excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData({ type: '', unit: 'Litro' });
    setEditingId(null);
    unsavedChanges.resetChanges();
  };

  // Função para lidar com mudanças no formulário
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    unsavedChanges.markAsChanged();
  };

  // Função para tentar fechar o dialog
  const handleDialogClose = (open) => {
    if (!open && unsavedChanges.hasChanges) {
      unsavedChanges.tryAction(() => {
        setDialogOpen(false);
        resetForm();
      });
    } else {
      setDialogOpen(open);
      if (!open) resetForm();
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;

  return (
    <div className="space-y-6" data-testid="combustiveis-page">
      {/* Diálogo de confirmação para sair sem salvar */}
      <UnsavedChangesDialog
        isOpen={unsavedChanges.showDialog}
        onOpenChange={unsavedChanges.setShowDialog}
        onConfirmLeave={unsavedChanges.confirmLeave}
      />
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Combustíveis</h2>
          <p className="text-slate-600">Tipos de combustível</p>
        </div>
        {canEdit ? (
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-combustivel-button">
                <Plus className="mr-2" size={20} />Novo Combustível
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingId ? 'Editar' : 'Novo'} Combustível</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="combustivel-form">
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Input value={formData.type} onChange={(e) => handleFormChange('type', e.target.value)} required data-testid="combustivel-type-input" placeholder="Ex: Gasolina Comum, Diesel S-10" />
                </div>
                <div className="space-y-2">
                  <Label>Unidade *</Label>
                  <Input value={formData.unit} onChange={(e) => handleFormChange('unit', e.target.value)} required data-testid="combustivel-unit-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => handleDialogClose(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="combustivel-submit-button">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        ) : (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
            <Lock size={18} />
            <span className="text-sm font-medium">Modo Visualização</span>
          </div>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Lista de Combustíveis</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Unidade</TableHead>
                {canEdit && <TableHead className="text-right">Ações</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {combustiveis.map((item) => (
                <TableRow key={item.id} className="table-row" data-testid={`combustivel-row-${item.id}`}>
                  <TableCell className="font-medium">{item.type}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(item)} data-testid={`edit-combustivel-${item.id}`}><Pencil size={16} /></Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-combustivel-${item.id}`}><Trash2 size={16} /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {combustiveis.length === 0 && (
                <TableRow><TableCell colSpan={canEdit ? 3 : 2} className="text-center py-8 text-slate-500">Nenhum combustível cadastrado</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
